import React from 'react';
import { Heart, Users, Briefcase, Home, UserX, Sparkles, Lock, MessageSquare, Crown, Zap, CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';
export type AnalysisMode = 'default' | 'relationships' | 'ex' | 'friends' | 'family' | 'workplace' | 'romantic' | 'difference';
interface ModeOption {
  value: AnalysisMode;
  label: string;
  icon: React.ReactNode;
  hint: string;
  isPremium: boolean;
  isGold?: boolean;
}
const modeOptions: ModeOption[] = [{
  value: 'default',
  label: 'General Analysis',
  icon: <MessageSquare size={18} data-id="element-3364" />,
  hint: 'Standard toxicity check for any conversation',
  isPremium: false
}, {
  value: 'difference',
  label: 'Difference Mode',
  icon: <Zap size={18} data-id="element-3365" />,
  hint: 'Advanced AI comparison of communication patterns over time',
  isPremium: true,
  isGold: true
}, {
  value: 'relationships',
  label: 'Dating/Relationships',
  icon: <Heart size={18} data-id="element-3366" />,
  hint: 'For current partners & general dating',
  isPremium: true
}, {
  value: 'ex',
  label: 'Ex-Partner',
  icon: <UserX size={18} data-id="element-3367" />,
  hint: 'For analysis of lingering patterns with an ex',
  isPremium: true
}, {
  value: 'friends',
  label: 'Friends',
  icon: <Users size={18} data-id="element-3368" />,
  hint: 'For group chats, gossip, and social dynamics',
  isPremium: true
}, {
  value: 'family',
  label: 'Family',
  icon: <Home size={18} data-id="element-3369" />,
  hint: 'For guilt-tripping, boundary issues, and family drama',
  isPremium: true
}, {
  value: 'workplace',
  label: 'Workplace',
  icon: <Briefcase size={18} data-id="element-3370" />,
  hint: 'For Slack, email, microaggressions, and professional chats',
  isPremium: true
}, {
  value: 'romantic',
  label: 'Romantic (Early Dating)',
  icon: <Sparkles size={18} data-id="element-3371" />,
  hint: 'For initial dating and love-bombing detection',
  isPremium: true
}];
interface ModeSelectorProps {
  selectedMode: AnalysisMode;
  onModeChange: (mode: AnalysisMode) => void;
  disabled?: boolean;
  isPro?: boolean;
  hasCredits?: boolean;
  onUpgradeTrigger?: () => void;
}
export function ModeSelector({
  selectedMode,
  onModeChange,
  disabled = false,
  isPro = false,
  hasCredits = false,
  onUpgradeTrigger
}: ModeSelectorProps) {
  const isUnlocked = (option: ModeOption) => {
    if (!option.isPremium) return true;
    // Gold features might have specific logic in future, for now treating as premium
    return isPro || hasCredits;
  };
  const handleModeClick = (option: ModeOption) => {
    if (disabled) return;
    if (isUnlocked(option)) {
      onModeChange(option.value);
    } else {
      onUpgradeTrigger?.();
    }
  };
  const getCardStyles = (option: ModeOption, isSelected: boolean, unlocked: boolean) => {
    if (isSelected) {
      if (option.isGold) return 'border-amber-400 bg-amber-50 ring-1 ring-amber-400';
      if (option.isPremium) return 'border-red-500 bg-red-50 ring-1 ring-red-500';
      return 'border-gray-400 bg-gray-100 ring-1 ring-gray-400';
    }
    if (unlocked) {
      return 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50';
    }
    return 'border-gray-100 bg-gray-50/50 opacity-70 hover:bg-gray-100';
  };
  const getIconStyles = (option: ModeOption, isSelected: boolean, unlocked: boolean) => {
    if (isSelected) {
      if (option.isGold) return 'bg-amber-100 text-amber-700';
      if (option.isPremium) return 'bg-red-100 text-red-700';
      return 'bg-gray-200 text-gray-800';
    }
    if (unlocked) {
      if (option.isGold) return 'bg-amber-50 text-amber-600 group-hover:bg-amber-100';
      return 'bg-gray-100 text-gray-600 group-hover:bg-gray-200';
    }
    return 'bg-gray-100 text-gray-400';
  };
  return <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm" data-id="element-3372">
      <div className="flex items-center justify-between mb-4" data-id="element-3373">
        <div data-id="element-3374">
          <label className="block text-sm font-bold text-gray-900 mb-1" data-id="element-3375">
            Choose Analysis Context
          </label>
          <p className="text-xs text-gray-500" data-id="element-3376">
            Select the relationship type for more accurate toxicity detection
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-3377">
        {modeOptions.map(option => {
        const unlocked = isUnlocked(option);
        const isSelected = selectedMode === option.value;
        return <motion.button key={option.value} type="button" whileHover={!disabled ? {
          scale: 1.01
        } : {}} whileTap={!disabled ? {
          scale: 0.99
        } : {}} onClick={() => handleModeClick(option)} disabled={disabled} className={`
                relative flex items-start gap-3 p-4 rounded-xl border transition-all text-left group
                ${getCardStyles(option, isSelected, unlocked)}
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `} data-id="element-3378">
              <div className={`
                flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-colors mt-0.5
                ${getIconStyles(option, isSelected, unlocked)}
              `} data-id="element-3379">
                {option.icon}
              </div>

              <div className="flex-1 min-w-0" data-id="element-3380">
                <div className="flex items-center gap-2 mb-1" data-id="element-3381">
                  <span className={`
                    font-bold text-sm transition-colors
                    ${isSelected ? 'text-gray-900' : unlocked ? 'text-gray-900' : 'text-gray-500'}
                  `} data-id="element-3382">
                    {option.label}
                  </span>

                  {option.isGold && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-200 uppercase tracking-wide" data-id="element-3383">
                      Gold
                    </span>}

                  {!unlocked && <Lock size={12} className="text-gray-400" data-id="element-3384" />}
                </div>

                <div className={`text-xs leading-relaxed ${isSelected ? 'text-gray-700' : 'text-gray-500'}`} data-id="element-3385">
                  {option.hint}
                </div>
              </div>

              {isSelected && <div className="absolute top-3 right-3" data-id="element-3386">
                  <CheckCircle2 size={18} className={option.isGold ? 'text-amber-600' : option.isPremium ? 'text-red-600' : 'text-gray-600'} fill="currentColor" color="white" data-id="element-3387" />
                </div>}
            </motion.button>;
      })}
      </div>
    </div>;
}